<template>
	<div class="modal-card pam-card">
		<header class="modal-card-head pam-head">
			<div>
				<p class="pam-eyebrow">Libro mayor</p>
				<h3 class="pam-title">Cuentas de remuneraciones</h3>
			</div>
			<button type="button" class="delete" aria-label="Cerrar" @click="$emit('close')"></button>
		</header>

		<section class="modal-card-body pam-body">
			<p v-if="loading" class="pam-muted">Cargando las cuentas…</p>
			<p v-else-if="loadError" class="pam-error" role="alert">{{ loadError }}</p>
			<PayrollAccountsSection
				v-else
				:accounts="accounts"
				:markedIds="markedIds"
				:outsideCount="outsideCount"
				:saving="saving"
				:locked="locked"
				embedded
				@change="onChange"
			/>
			<p v-if="saveError" class="pam-error" role="alert">{{ saveError }}</p>
		</section>

		<footer class="modal-card-foot pam-foot">
			<p class="pam-note">
				<template v-if="locked">Cuentas bloqueadas junto con el plan de cuentas.</template>
				<template v-else>Al guardar, el reporte se recalcula con los cambios.</template>
			</p>
			<div class="pam-foot__actions">
				<button type="button" class="button pam-btn pam-btn--ghost" @click="$emit('close')">
					Volver
				</button>
				<button
					v-if="!locked"
					type="button"
					class="button pam-btn pam-btn--primary"
					:disabled="!canSave"
					@click="save"
				>
					{{ saving ? "Guardando…" : "Guardar" }}
				</button>
			</div>
		</footer>
	</div>
</template>

<script>
// Cuentas de remuneraciones del mayor (decisiones §40): modal del panel del
// analista, abierto desde el aside de la vista del mayor para no quitarle
// espacio al flujo de subida. Carga y guarda por su cuenta a través de la
// API; `PayrollAccountsSection` (embebida) dibuja las casillas y avisa cada
// cambio. Misma receta que `UnlockAccountPlanModal` (tokens --ap-*).
import PayrollAccountsSection from "@/components/companyInfo/PayrollAccountsSection";
import {
	getLedgerPayrollAccounts,
	saveLedgerPayrollAccounts,
} from "@/api/carteraAnalista";
import {
	consoleHandlerError,
	extractErrorMessage,
	notificate,
} from "@/utils/message";

export default {
	name: "PayrollAccountsModal",
	components: { PayrollAccountsSection },
	props: {
		companyId: { type: Number, required: true },
		// Candado del plan de cuentas (§30): solo lectura.
		locked: { type: Boolean, default: false },
	},
	data() {
		return {
			loading: false,
			loadError: "",
			saving: false,
			saveError: "",
			accounts: [],
			markedIds: [],
			selected: [],
			outsideCount: 0,
		};
	},
	computed: {
		dirty() {
			return this.selected.join(",") !== this.sorted(this.markedIds).join(",");
		},
		canSave() {
			return this.dirty && !this.saving && !this.locked;
		},
	},
	created() {
		this.load();
	},
	methods: {
		load() {
			this.loading = true;
			this.loadError = "";
			return getLedgerPayrollAccounts(this.companyId)
				.then(({ data }) => this.apply(data))
				.catch((error) => {
					this.loadError = extractErrorMessage(
						error, "No se pudieron leer las cuentas. Vuelve a intentarlo.",
					);
					consoleHandlerError({ error, silent: true });
				})
				.finally(() => {
					this.loading = false;
				});
		},
		apply(data) {
			this.accounts = data.accounts || [];
			this.markedIds = this.accounts
				.filter((account) => account.is_payroll)
				.map((account) => account.id);
			this.selected = this.sorted(this.markedIds);
			this.outsideCount = data.payroll_documents_outside_rrhh || 0;
		},
		onChange(ids) {
			this.selected = this.sorted(ids);
		},
		save() {
			if (!this.canSave) return;
			this.saving = true;
			this.saveError = "";
			return saveLedgerPayrollAccounts(this.companyId, this.selected)
				.then(({ data }) => {
					this.apply(data);
					notificate(
						"<strong>Cuentas de remuneraciones guardadas</strong><br>El reporte se recalcula con los cambios.",
						"is-success is-light"
					);
					this.$emit("saved");
					this.$emit("close");
				})
				.catch((error) => {
					// 400 (id fuera del plan) o 403 (acumulada / candado): el
					// backend ya dice qué pasó y qué hacer.
					this.saveError = extractErrorMessage(
						error, "No se pudieron guardar las cuentas de remuneraciones. Vuelve a intentarlo.",
					);
					consoleHandlerError({ error, silent: true });
				})
				.finally(() => {
					this.saving = false;
				});
		},
		sorted(ids) {
			return [...ids].sort((a, b) => a - b);
		},
	},
};
</script>

<style scoped lang="scss">
// Tema del analista (var(--ap-*), elements/_analyst_theme.scss).
.pam-card {
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
	width: min(760px, 94vw);
	display: flex;
	flex-direction: column;
	max-height: 86vh;
	background-color: var(--ap-paper);
	border-radius: var(--ap-radius);
	overflow: hidden;
}

.pam-head {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	background-color: var(--ap-paper);
	border-bottom: 1px solid var(--ap-line);
}

.pam-eyebrow {
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

.pam-title {
	font-size: 20px;
	font-weight: 700;
	letter-spacing: -0.01em;
	color: var(--ap-ink);
}

.pam-body {
	background-color: var(--ap-paper);
}

.pam-muted {
	font-size: 13px;
	color: var(--ap-muted);
}

.pam-error {
	margin-top: 0.75rem;
	padding: 0.5rem 0.75rem;
	border: 1px solid var(--ap-alert);
	border-radius: var(--ap-radius-sm);
	background: var(--ap-alert-wash);
	color: var(--ap-alert);
	font-size: 13px;
	line-height: 20px;
}

.pam-foot {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	background-color: var(--ap-paper);
	border-top: 1px solid var(--ap-line);

	&__actions {
		display: flex;
		gap: 0.5rem;
	}
}

.pam-note {
	font-size: 12px;
	color: var(--ap-muted);
}

.pam-btn {
	font-family: var(--ap-font);
	font-weight: 600;
	border-radius: var(--ap-radius-pill);

	&--ghost {
		background: var(--ap-paper);
		border-color: var(--ap-line-strong);
		color: var(--ap-ink);
	}

	&--primary {
		background-color: var(--ap-ink);
		border-color: transparent;
		color: var(--ap-paper);

		&:hover:not(:disabled) {
			background-color: var(--ap-teal-deep);
		}
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}
</style>
